import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { BreadcrumbService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { JournalEntry, JournalEntryStatus, JournalLineItem } from '../../../models/journal-entry/journal-entry';
import { JournalEntryProxyService } from '../../../proxies/journal-entry.proxy.service';
import { EnumService } from '../../../services/enum.service';

/**
 * Journal Entry details (Task #5026) — the full posting behind the condensed summary that every source
 * document shows (#5024).
 *
 * Read-only. Entries are written by `GlPostingHelper` when their source document posts; nothing on this
 * screen can change one, so there is no form, no save and no status action.
 */
@Component({
  selector: 'app-journal-entry-details',
  templateUrl: './journal-entry-details.component.html',
  styleUrl: './journal-entry-details.component.scss',
})
export class JournalEntryDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(JournalEntryProxyService);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  headerTitle = signal<string>(this.t('JournalEntry', 'Journal Entry'));

  entry = signal<JournalEntry | null>(null);

  /** Enum labels are read once the service reports ready; until then every label renders empty, not wrong. */
  private statusLabels: Record<number, string> = {};
  private sourceModuleLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  readonly hasEntry = computed(() => !!this.entry());

  /** Only worth showing the conversion line when the entry was not booked in the books' own currency. */
  readonly isForeignCurrency = computed(() => {
    const entry = this.entry();
    return !!entry && entry.currency !== entry.functionalCurrency;
  });

  /**
   * The books balance in the FUNCTIONAL currency — the only currency every line shares (#5144).
   *
   * <p>A per-line-rate entry debits Lempiras and credits dollars in one posting; its raw transaction
   * totals are sums across currencies and comparing them declared every such entry "unbalanced" while the
   * GL had it balanced to the cent. The functional totals are what the GL actually enforces. Entries from
   * before functional amounts existed carry zeros there, so those fall back to the transaction compare
   * that was always right for them.</p>
   */
  readonly isBalanced = computed(() => {
    const entry = this.entry();
    if (!entry) return false;
    const functionalDebits = entry.totalFunctionalDebits ?? 0;
    const functionalCredits = entry.totalFunctionalCredits ?? 0;
    if (functionalDebits > 0 || functionalCredits > 0) return Math.abs(functionalDebits - functionalCredits) < 0.005;
    return entry.totalDebits === entry.totalCredits;
  });

  /** True when the lines do not all share the entry's currency — an AP payment settling foreign invoices
   *  (#5144). Transaction totals are then sums across currencies, so the screen totals functionally. */
  readonly isMixedCurrency = computed(() => {
    const entry = this.entry();
    if (!entry?.lineItems) return false;
    return entry.lineItems.some((line) => (line.currency != null && line.currency !== entry.currency) || this.isFunctionalOnly(line));
  });

  /** A line with no transaction amounts at all — the realized exchange difference, which exists only in
   *  the books' currency: no Lempiras moved and no dollars moved, the difference is what the movement was
   *  worth (#5144). Rendered from its functional figures, or it renders as nothing. */
  isFunctionalOnly(line: JournalLineItem): boolean {
    return !line.debitAmount && !line.creditAmount && (!!line.functionalDebitAmount || !!line.functionalCreditAmount);
  }

  /** The line's own currency label, only when it differs from the entry's — a same-currency line's figures
   *  are already read under the entry's header and labeling them again would be noise. */
  lineCurrencyText(line: JournalLineItem): string {
    const entry = this.entry();
    if (!entry || line.currency == null || line.currency === entry.currency) return '';
    return this.currencyLabels[line.currency] ?? '';
  }

  /** The currency a line's transaction amount is actually stated in — its own when it carries one, the
   *  entry's otherwise. */
  private effectiveCurrency(line: JournalLineItem): number {
    return line.currency ?? this.entry()?.currency ?? 0;
  }

  /** That currency's label, always — unlike {@link lineCurrencyText}, which stays silent when the line
   *  agrees with its entry. Used where the amount is being labelled rather than flagged as unusual. */
  effectiveCurrencyText(line: JournalLineItem): string {
    return this.currencyLabels[this.effectiveCurrency(line)] ?? '';
  }

  /**
   * True when this line moved money in something other than the books' currency — so the books' figure
   * leads and what actually moved is stated beneath it.
   *
   * <p>Asked against the FUNCTIONAL currency rather than the entry's. An all-Lempira entry under dollar
   * books has no line that disagrees with its entry, and still needs converting for a reader who works in
   * dollars; comparing against the entry would have left exactly that case unconverted.</p>
   *
   * <p>False for entries written before functional amounts existed (#4549): they carry none, so there is
   * nothing to lead with and the transaction figure stays the only figure.</p>
   */
  showsConversion(line: JournalLineItem): boolean {
    if (this.isFunctionalOnly(line)) return false;
    if (this.effectiveCurrency(line) === (this.entry()?.functionalCurrency ?? 0)) return false;
    return !!line.functionalDebitAmount || !!line.functionalCreditAmount;
  }

  /**
   * A line's rate the way a person quotes one: units of the line's currency per 1 unit of the books'
   * currency — `25.84 HNL / USD`, the number read off the wire confirmation and typed in.
   *
   * <p><b>The stored figure is the other way up.</b> `line.exchangeRate` is the multiplier the GL posts
   * with, functional per line-unit, and for Lempiras that is 0.038700 — arithmetically correct, and not a
   * number anybody recognises or can check against a bank advice. Everywhere else in this system a rate is
   * quoted on the USD pivot (`ApDocument.RateToUsd`, a PO's `ExchangeRateToUSD`,
   * `Finance:ExchangeRateBounds`), so showing the reciprocal made this screen speak a different dialect
   * from every other.</p>
   *
   * <p>The inversion is exact both ways, so it stays right for a company whose books are not in dollars: a
   * USD line under Lempira books reads `0.038700 USD / HNL`, again the quote a person would say.</p>
   */
  lineRateText(line: JournalLineItem): string {
    const rate = line.exchangeRate ?? 0;
    const lineCurrency = this.effectiveCurrencyText(line);
    if (rate <= 0 || !lineCurrency) return '';

    // Six places matches how a rate is captured; trailing zeroes are trimmed so an ordinary 25.84 reads as
    // 25.84 rather than 25.840000.
    return `${Number((1 / rate).toFixed(6))} ${lineCurrency} / ${this.functionalCurrencyText()}`;
  }

  readonly periodLabel = computed(() => {
    const entry = this.entry();
    if (!entry?.fiscalYear || !entry?.fiscalPeriod) return '';
    return `${entry.fiscalYear}-${String(entry.fiscalPeriod).padStart(2, '0')}`;
  });

  readonly statusText = computed(() => this.statusLabels[this.entry()?.status ?? 0] ?? '');
  readonly sourceModuleText = computed(() => this.sourceModuleLabels[this.entry()?.sourceModule ?? 0] ?? '');
  /**
   * Whether the footer should carry the entry's own totals alongside the books' ones.
   *
   * <p>Only when the entry is in ONE currency and that currency is not the books' — then the transaction
   * total is a real, single-currency figure and worth reading beside the converted one. A mixed entry's
   * transaction total is a sum across currencies (10,000 Lempiras plus 357.14 dollars is not a number), and
   * a same-currency entry would just print the identical figure twice.</p>
   */
  readonly showsTransactionTotals = computed(() => {
    const entry = this.entry();
    return !!entry && !this.isMixedCurrency() && entry.currency !== entry.functionalCurrency;
  });

  readonly currencyText = computed(() => this.currencyLabels[this.entry()?.currency ?? 0] ?? '');
  readonly functionalCurrencyText = computed(() => this.currencyLabels[this.entry()?.functionalCurrency ?? 0] ?? '');

  readonly statusSeverity = computed<'success' | 'warn' | 'danger' | 'secondary'>(() => {
    switch (this.entry()?.status) {
      case JournalEntryStatus.Posted:
        return 'success';
      case JournalEntryStatus.Draft:
        return 'warn';
      case JournalEntryStatus.Reversed:
        return 'danger';
      default:
        return 'secondary';
    }
  });

  ngOnInit(): void {
    this.statusLabels = this.enumService.getEnum('JournalEntryStatus').values as Record<number, string>;
    this.sourceModuleLabels = this.enumService.getEnum('PostingModule').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;

    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.load(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private load(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.apply(response?.data ?? null),
        error: () => this.entry.set(null),
      });
  }

  private apply(entry: JournalEntry | null): void {
    this.entry.set(entry);
    if (!entry) return;
    this.headerTitle.set(entry.number);
    this.breadcrumbService.setCurrent(entry.number);
  }

  /**
   * Follows a reversal cross-link. Both sides name each other by number, not id, so the number is resolved
   * within this entry's own company before navigating — the same document number can exist in another.
   */
  openByNumber(documentNumber: string | null | undefined): void {
    const company = this.entry()?.financialCompanyReference;
    if (!documentNumber || !company) return;

    this.startLoading();
    this.service
      .getByDocumentNumber(company, documentNumber)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const target = response?.data;
          if (!target?.id) return;
          // Same route, different id — reload rather than rely on the component being re-created.
          this.router.navigate(['/journal-entry-details'], { queryParams: { id: target.id } }).then(() => {
            this.currentId.set(target.id!);
            this.load(target.id!);
          });
        },
      });
  }

  back(): void {
    this.router.navigate(['/journal-entries']);
  }
}
